import { PURCHASE_SELECTOR } from "./discovery"

// How long to wait for a late-rendered buy box before giving up on this page.
const WATCH_MS = 10_000
const RECHECK_MS = 100

export interface Anchor {
  element: Element
  position: "inside" | "after"
}

export interface PlaceableWidget extends HTMLElement {
  hasProduct(): boolean
  setPlaced(placed: boolean): void
  reportUnplaced(): void
}

// Where the CTA belongs: an explicit target or [data-vc-mount] receives it as a child;
// otherwise right after the first visible add-to-cart control (themes may carry hidden
// duplicates in modals or mobile variants). No match means no good spot on this page.
export function findAnchor(selector?: string): Anchor | null {
  const container = safeQuery(selector) || document.querySelector("[data-vc-mount]")
  if (container) return { element: container, position: "inside" }

  const control = [ ...document.querySelectorAll(PURCHASE_SELECTOR) ].find(isVisible)
  return control ? { element: besideOrBelow(control), position: "after" } : null
}

// A buy button in a horizontal row would put the CTA beside it, squeezed and misaligned
// (seen on a live store), so go below the whole row instead.
function besideOrBelow(control: Element): Element {
  const parent = control.parentElement
  if (!parent || parent === document.body) return control
  const { display, flexDirection } = getComputedStyle(parent)
  const isRow = (display === "flex" || display === "inline-flex") && !flexDirection.startsWith("column")
  return isRow ? parent : control
}

function safeQuery(selector?: string): Element | null {
  try {
    return selector ? document.querySelector(selector) : null
  } catch (_error) {
    return null // A merchant's invalid data-target must not stop the widget booting.
  }
}

// getClientRects, not offsetParent: a position:fixed sticky buy bar has no offsetParent.
function isVisible(element: Element): boolean {
  return element.getClientRects().length > 0
}

function attach(widget: Element, anchor: Anchor) {
  if (anchor.position === "inside") {
    if (widget.parentElement !== anchor.element) anchor.element.appendChild(widget)
  } else if (anchor.element.nextElementSibling !== widget) {
    anchor.element.insertAdjacentElement("afterend", widget)
  }
}

// Places the widget and keeps trying while the page renders. The end of <body> only
// hosts the modal and status pill: the CTA stays hidden there, and a product page that
// never offers a spot is reported to the operator. Returns a function that re-runs this
// when the page's product changes.
export function keepPlaced(widget: PlaceableWidget, selector?: string, watchMs = WATCH_MS): () => void {
  let observer: MutationObserver | null = null
  let giveUp: ReturnType<typeof setTimeout> | undefined
  let recheck: ReturnType<typeof setTimeout> | undefined

  const stop = () => {
    observer?.disconnect()
    observer = null
    clearTimeout(giveUp)
    clearTimeout(recheck)
    recheck = undefined
  }

  const tryPlace = () => {
    const anchor = findAnchor(selector)
    if (!anchor) return false
    attach(widget, anchor)
    widget.setPlaced(true)
    stop()
    return true
  }

  const start = () => {
    stop()
    if (tryPlace()) return
    if (widget.parentElement !== document.body) document.body.appendChild(widget)
    widget.setPlaced(false)
    if (!widget.hasProduct()) return

    observer = new MutationObserver(() => {
      recheck ??= setTimeout(() => {
        recheck = undefined
        tryPlace()
      }, RECHECK_MS)
    })
    observer.observe(document.body, {
      childList: true, subtree: true, attributes: true, attributeFilter: [ "class", "style", "hidden" ]
    })
    giveUp = setTimeout(() => {
      if (!tryPlace()) {
        stop()
        widget.reportUnplaced()
      }
    }, watchMs)
  }

  start()
  return start
}
